import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { createSelector } from 'reselect';
import IBaseState from '../../types/IBaseState';
import { AppState } from '../../reducers/root.reducer';
import AdminReportsService, {
    BasePayload,
    ResultPayload,
} from '../services/AdminReportsService';
import { RequestServiceResponse } from '@tcc-online/request-service';
import { createNotifiedThunk } from '../../reducers/createAppAsyncThunk';
import { Report, ReportResult } from '@tcc-online/output-report';

const NAME = 'AdminReports';

const getReports = createNotifiedThunk(
    `${NAME}/getReports`,
    AdminReportsService.getReports,
    { error: 'fe.admin.error.load-reports' },
);
const getResult = createNotifiedThunk(
    `${NAME}/getResult`,
    AdminReportsService.getResult,
    { error: 'fe.admin.error.load-result' },
);
export interface GetResultsPayload extends BasePayload {
    reportIds: number[];
    results: ReportResult[];
}

function wait(ms: number): Promise<void> {
    return new Promise((resolve) => {
        setTimeout(resolve, ms);
    });
}

const getResultComparison = createNotifiedThunk<
    RequestServiceResponse<ReportResult>,
    ResultPayload
>(`${NAME}/getResultComparison`, AdminReportsService.getResult, {
    error: 'fe.admin.error.load-result',
});

const getResults = createNotifiedThunk<ReportResult[], GetResultsPayload>(
    `${NAME}/getResults`,
    async (payload, { dispatch }) => {
        const { results, productType, projectId, reportIds, jobMatch } =
            payload;
        const data: ReportResult[] = [];

        for (let index = 0; index < results.length; index++) {
            if (index > 0) {
                await wait(100);
            }

            dispatch(
                getResultComparison({
                    productType,
                    projectId,
                    reportId: reportIds[index],
                    jobMatch,
                    resultId: results[index].id,
                }),
            );
        }

        return data;
    },
    { error: 'fe.admin.error.load-result' },
);

export type ComparisonResultsState = {
    data: ReportResult[];
    loading: Record<number, boolean>;
};

export type AdminReportsState = {
    reports: IBaseState<Report[]>;
    result: IBaseState<ReportResult | null>;
    comparisonResults: ComparisonResultsState;
};

const initialState: AdminReportsState = {
    reports: { data: [], loading: false },
    result: { data: null, loading: false },
    comparisonResults: { data: [], loading: {} },
};

const slice = createSlice({
    name: NAME,
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder.addCase(
            getReports.pending,
            (state: AdminReportsState): void => {
                state.reports.loading = true;
            },
        );
        builder.addCase(
            getReports.fulfilled,
            (
                state: AdminReportsState,
                action: PayloadAction<RequestServiceResponse<Report[]>>,
            ): void => {
                state.reports.data = action.payload.data;
                state.reports.loading = false;
            },
        );
        builder.addCase(
            getReports.rejected,
            (state: AdminReportsState): void => {
                state.reports.loading = false;
            },
        );

        builder.addCase(getResult.pending, (state: AdminReportsState): void => {
            state.result.loading = true;
        });
        builder.addCase(
            getResult.fulfilled,
            (
                state: AdminReportsState,
                action: PayloadAction<RequestServiceResponse<ReportResult>>,
            ): void => {
                state.result.data = action.payload.data;
                state.result.loading = false;
            },
        );
        builder.addCase(
            getResult.rejected,
            (state: AdminReportsState): void => {
                state.result.loading = false;
            },
        );

        builder.addCase(
            getResultComparison.fulfilled,
            (
                state: AdminReportsState,
                action: PayloadAction<
                    RequestServiceResponse<ReportResult>,
                    string,
                    { arg: ResultPayload }
                >,
            ): void => {
                state.comparisonResults.data.push(action.payload.data);
                state.comparisonResults.loading[action.meta.arg.resultId] =
                    false;
            },
        );
        builder.addCase(
            getResultComparison.rejected,
            (
                state: AdminReportsState,
                action: { meta: { arg: ResultPayload } },
            ): void => {
                state.comparisonResults.loading[action.meta.arg.resultId] =
                    false;
            },
        );

        builder.addCase(
            getResults.pending,
            (
                state: AdminReportsState,
                action: { meta: { arg: GetResultsPayload } },
            ): void => {
                state.comparisonResults.data = [];
                state.comparisonResults.loading = Object.fromEntries(
                    action.meta.arg.results.map((result) => [result.id, true]),
                );
            },
        );
        builder.addCase(getResults.fulfilled, (): void => {});
        builder.addCase(
            getResults.rejected,
            (state: AdminReportsState): void => {
                Object.keys(state.comparisonResults.loading).forEach(
                    (resultId) => {
                        state.comparisonResults.loading[Number(resultId)] =
                            false;
                    },
                );
            },
        );
    },
});

const state = (state: AppState): AdminReportsState => state.admin.reports;

const reportId = (_: AppState, reportId: number) => reportId;
const all = state;
const reports = createSelector(
    all,
    (state: AdminReportsState) => state.reports.data,
);

const AdminReportsSlice = {
    reducer: slice.reducer,
    actions: {
        getReports,
        getResult,
        getResultComparison,
        getResults,
    },
    state: {
        all: all,
        reports: createSelector(
            all,
            (state: AdminReportsState) => state.reports,
        ),
        detail: createSelector(
            [reports, reportId],
            (reports: Report[], reportId: number) =>
                reports.find((report: Report) => report.id === reportId),
        ),
        result: createSelector(all, (state: AdminReportsState) => state.result),
        comparisonResults: createSelector(
            all,
            (state: AdminReportsState) => state.comparisonResults,
        ),
    },
};

export default AdminReportsSlice;
